import React, { useState, useEffect } from 'react';
import { getAllTables, addCultivarsToTable } from '../services/api';

function AddCultivars({ crop }) {
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadTables();
  }, [crop]);

  const loadTables = async () => {
    try {
      const response = await getAllTables(crop);
      setTables(response.tables || []);
    } catch (error) {
      console.error('Error loading tables:', error);
      setError('Failed to load tables');
    }
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    if (!selectedTable) {
      setError('Please select a table');
      return;
    }

    setUploading(true);
    setError(null);
    setResult(null);

    try {
      const response = await addCultivarsToTable(selectedTable, file);
      setResult(response);
      setFile(null);
      // Reset file input
      document.getElementById('cultivar-file-input').value = '';
    } catch (error) {
      console.error('Error adding cultivars:', error);
      setError(error.response?.data?.detail || 'Failed to add cultivars');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="file-upload-container">
      <h2>Add New Cultivars</h2>
      <p className="description">
        Upload a file with a 'marker' column and new cultivar data. 
        The system will match markers and add new columns to your existing table.
      </p>

      <div className="upload-form">
        <div className="form-group">
          <label htmlFor="table-select">Select Table:</label>
          <select
            id="table-select"
            value={selectedTable}
            onChange={(e) => setSelectedTable(e.target.value)}
            disabled={uploading}
            className="table-select"
          >
            <option value="">-- Select a table --</option>
            {tables.map((table) => (
              <option key={table} value={table}>
                {table}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="cultivar-file-input">Choose File (CSV or Excel):</label>
          <input
            id="cultivar-file-input"
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileChange}
            disabled={uploading}
          />
          {file && <p className="file-name">Selected: {file.name}</p>}
        </div>

        <button
          onClick={handleUpload}
          disabled={uploading || !file || !selectedTable}
          className="upload-button"
        >
          {uploading ? 'Processing...' : 'Add Cultivars'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      {result && result.has_duplicates && (
        <div className="error-message" style={{ backgroundColor: '#fff3cd', borderColor: '#ffc107', color: '#856404' }}>
          <h3>⚠️ Duplicates Found!</h3>
          <div className="result-details">
            <p><strong>Total in file:</strong> {result.total_in_file}</p>
            <p><strong>Duplicates found:</strong> {result.duplicates_found}</p>
            <p><strong>New cultivars:</strong> {result.new_cultivars}</p>
            <p style={{ marginTop: '10px', fontWeight: 'bold' }}>
              The following cultivars already exist in the database:
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px', fontSize: '0.85em' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8d7da' }}>
                  <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'left' }}>Cultivar Name</th>
                  <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'left' }}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {result.duplicate_details && result.duplicate_details.map((dup, idx) => (
                  <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                    <td style={{ border: '1px solid #ddd', padding: '6px' }}>{dup.name}</td>
                    <td style={{ border: '1px solid #ddd', padding: '6px' }}>{dup.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ marginTop: '10px', color: '#dc3545' }}>
              ⛔ No cultivars were added. Please remove duplicates from your file and try again.
            </p>
          </div>
        </div>
      )}

      {result && !result.has_duplicates && (
        <div className="success-message">
          <h3>✓ Success!</h3>
          <div className="result-details">
            <p><strong>Table:</strong> {result.table_name}</p>
            {result.rows_inserted !== undefined && <p><strong>Rows Inserted:</strong> {result.rows_inserted}</p>}
            {result.rows_updated !== undefined && <p><strong>Rows Updated:</strong> {result.rows_updated}</p>}
            {result.rows_marked_failed !== undefined && <p><strong>Rows Marked as Failed:</strong> {result.rows_marked_failed}</p>}
            <p><strong>Total Processed:</strong> {result.total_processed}</p>
            {result.new_cultivar_names && result.new_cultivar_names.length > 0 && (
              <p><strong>New Cultivars:</strong> {result.new_cultivar_names.join(', ')}</p>
            )}
            {result.new_columns_added && result.new_columns_added.length > 0 && (
              <p><strong>New Columns Added:</strong> {result.new_columns_added.join(', ')}</p>
            )}
          </div>
        </div>
      )}

      <div className="info-box">
        <h4>Things to remember:</h4>
        <ul>
          <li>The system will <strong>check for duplicate cultivars</strong> before adding</li>
          <li>If duplicates are found, <strong>nothing is inserted</strong> — you'll see which names already exist</li>
          <li>Remove duplicates from your file and re-upload to add only new cultivars</li>
          <li>Upload format: CSV or Excel with sample data (one row per cultivar)</li>
        </ul>
      </div>
    </div>
  );
}

export default AddCultivars;
